// Copyright TraderEvolution Global LTD. © 2017-2024. All rights reserved.

import { Color } from '../../../Graphics.js';
import { PriceType } from '../../../../Utils/History/CashItemUtils';
import { MAMode } from '../IndicatorConstants.js';
import { IndicatorScriptBase } from '../IndicatorScriptBase';
import { LineStyle } from '../IndicatorScriptBaseEnums';
import { type iBuildInIndicator } from '../iBuildInIndicator';
import { InputParameterCombobox } from '../InputParamaterClasses/InputParameterCombobox';
import { InputParameterInteger } from '../InputParamaterClasses/InputParameterInteger';

export class PO extends IndicatorScriptBase {
    public MAType: number;
    public SourcePrice: number;
    public MAPeriod1: number;
    public MAPeriod2: number;

    private fastMA: iBuildInIndicator;
    private slowMA: iBuildInIndicator;

    constructor () {
        super();
        // #region Initialization
        this.ProjectName = 'Price Oscillator';
        this.Comments = 'The variation between price moving averages';
        // #endregion

        this.SetIndicatorLine('PO', Color.Blue, 1, LineStyle.SimpleChart);
        this.SetLevelLine('Zero', 0, Color.Gray, 1, LineStyle.SimpleChart);
        this.SeparateWindow = true;

        this.MAType = MAMode.SMA;
        super.InputParameter(new InputParameterCombobox('MAType', 'Type of Moving Average', 0,
            [
                { Simple: MAMode.SMA },
                { Exponential: MAMode.EMA },
                { Modified: MAMode.SMMA },
                { 'Linear Weighted': MAMode.LWMA }
            ]));

        this.SourcePrice = PriceType.Close;
        super.InputParameter(new InputParameterCombobox('SourcePrice', 'Sources prices for MA', 0,
            [
                { Close: PriceType.Close },
                { Open: PriceType.Open },
                { High: PriceType.High },
                { Low: PriceType.Low },
                { Typical: PriceType.Typical },
                { Medium: PriceType.Medium },
                { Weighted: PriceType.Weighted }
            ]));

        this.MAPeriod1 = 2;
        super.InputParameter(new InputParameterInteger('MAPeriod1', 'Period of MA1:', 0, 1, 99999, 0, 1));

        this.MAPeriod2 = 25;
        super.InputParameter(new InputParameterInteger('MAPeriod2', 'Period of MA2:', 0, 1, 99999, 0, 1));
    }

    public Init (): void {
        this.IndicatorShortName(this.GetIndicatorShortName());
    };

    public override GetIndicatorShortName (): string {
        return 'PO(' + this.MAPeriod1 + ', ' + this.MAPeriod2 + ')';
    };

    public override createInternalIndicators (): never[] {
        this.fastMA = this.Indicators.iMA(this.CurrentData, this.MAPeriod1, this.MAType, 0, this.SourcePrice);
        this.slowMA = this.Indicators.iMA(this.CurrentData, this.MAPeriod2, this.MAType, 0, this.SourcePrice);
        return [this.fastMA, this.slowMA] as never;
    };

    public OnQuote (): void {
        super.OnQuote();

        if (this.CurrentData.Count < this.MAPeriod1 || this.CurrentData.Count < this.MAPeriod2) { return; };

        this.SetValue(0, 0, this.fastMA.GetValue() - this.slowMA.GetValue());
    };
}
